import React from "react";
import styled from "styled-components";
import FormContainer from "../../../common/forms/FormContainer";
import LineChartUp01 from "@untitled-ui/icons-react/build/esm/LineChartUp01";
import PresentationChart01 from "@untitled-ui/icons-react/build/esm/PresentationChart01";
import Scale02 from "@untitled-ui/icons-react/build/esm/Scale02";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";
import File06 from "@untitled-ui/icons-react/build/esm/File06";
import TooltipSimple from "../../../common/tooltip/TooltipSimple";
import { formatBuyingIntelDate, formatBuyingIntelNumber, formatBuyingIntelPercentage } from "../buyingIntelDataUtils";

// #region SCOPED CSS
const StyledContextGrid = styled.div`
    &.context-grid {
        align-items: stretch;
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
    }

    /* --- Shared card base --- */
    .context-card {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        border-radius: 16px;
        padding: 10px 16px;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 16px;
        backdrop-filter: blur(50px);
        min-width: 0;
    }

    /* Preserve the approved proportions while wrapping from each card's minimum usable width. */
    .brand-info,
    .season-info {
        flex: 1.4 1 0;
        min-width: 230px;
    }

    .budget-info,
    .order-score-info {
        flex: 1 1 0;
        min-width: 150px;
    }

    .margin-info {
        flex: 2 1 0;
        min-width: 320px;
    }

    .actions-info {
        flex: 1.6 1 0;
        min-width: 230px;
    }

    /* --- Brand card: logo + details --- */
    .brand-logo {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        border-radius: 4px;
        padding: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 70px;
        align-self: stretch;
    }

    .brand-logo img {
        border-radius: 4px;
        width: 50px;
        height: 42px;
        object-fit: cover;
    }

    /* --- Info detail text blocks (brand info + comparable season) --- */
    .info-details {
        padding: 4px 10px;
        font-family: "Roboto-Regular", sans-serif;
        font-size: 14px;
        line-height: 18px;
        letter-spacing: 0.0025em;
        font-weight: 400;
        text-align: left;
        min-width: 0;
    }

    .info-label {
        color: var(--text-and-icons-gray-100, #d1d1d1);
    }

    .info-label + .info-value {
        margin-left: 4px;
    }

    .info-value {
        color: var(--text-and-icons-green-500, #00bb00);
    }

    /* --- KPI widget (single metric: label + value + delta) --- */
    .kpi-widget {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        min-width: 0;
    }

    .kpi-header {
        display: flex;
        flex-direction: row;
        gap: 6px;
        align-items: center;
        justify-content: center;
    }

    .kpi-label {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: center;
        font-family: var(--label-medium-uppercase-font-family, "Inter-Regular", sans-serif);
        font-size: var(--label-medium-uppercase-font-size, 12px);
        font-weight: var(--label-medium-uppercase-font-weight, 400);
        text-transform: uppercase;
        white-space: nowrap;
    }

    .kpi-info-icon {
        flex-shrink: 0;
        width: 10px;
        height: 10px;
        overflow: visible;
    }

    .kpi-value {
        color: var(--text-and-icons-green-500, #00bb00);
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .kpi-value-main {
        font-family: var(--h4-18-semibold-font-family, "Inter-SemiBold", sans-serif);
        font-size: var(--h4-18-semibold-font-size, 18px);
        line-height: var(--h4-18-semibold-line-height, 24px);
        font-weight: var(--h4-18-semibold-font-weight, 600);
    }

    .kpi-value-unit {
        font-family: var(--bodytext-14-regular-font-family, "Inter-Regular", sans-serif);
        font-size: var(--bodytext-14-regular-font-size, 14px);
        line-height: var(--bodytext-14-regular-line-height, 20px);
        font-weight: var(--bodytext-14-regular-font-weight, 400);
        margin-left: 4px;
    }

    .kpi-delta {
        color: var(--text-and-icons-green-500, #00bb00);
        text-align: center;
        font-family: var(--subtitle-2-font-family, "Roboto-Regular", sans-serif);
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
    }

    /* --- Order Score has an orange status instead of a delta --- */
    .kpi-status {
        text-align: center;
        font-family: var(--subtitle-2-font-family, "Roboto-Regular", sans-serif);
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
    }

    .kpi-status--warning {
        color: var(--text-and-icons-orange-500, #ff9900);
    }

    /* --- KPI pair container (turnover + gross margin side by side) --- */
    .kpi-pair {
        display: flex;
        flex-direction: row;
        gap: 6px;
        align-items: center;
        padding: 4px 10px;
        flex: 1;
        min-width: 0;
    }

    /* --- Actions card --- */
    .context-card--actions {
        padding: 24px 44px;
        justify-content: center;
    }

    .actions-row {
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: center;
    }

    .action-btn {
        background: var(--Buttons-All-Platforms-Gray-300, #1e2631);
        border-radius: 4px;
        border: 0;
        padding: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 35px;
        width: 35px;
        flex-shrink: 0;
        color: var(--text-icons-gray-300, #4f5b6d);
        cursor: not-allowed;
    }

    .action-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        overflow: visible;
    }

    /* --- Responsive: stack the double KPI content on small screens. --- */
    @media (max-width: 900px) {
        .kpi-pair {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
        }
    }
`;
// #endregion

export default function BuyingCycleContext({ data = {} }) {
    const {
        labelBrand = {},
        seasonName,
        comparableSeasonName,
        scope = {},
        requestedOn,
        stage,
        budget = {},
        estimatedTurnover = {},
        estimatedGrossMargin = {},
        orderScore = {}
    } = data || {};

    return (
        <FormContainer title="BUYING CYCLE CONTEXT">
            <StyledContextGrid className="context-grid">
                {/* Brand info card */}
                <div className="context-card brand-info">
                    <div className="brand-logo">
                        <img src={labelBrand.logoURL} alt={`${labelBrand.name || "Label brand"} logo`} />
                    </div>
                    <div className="info-details">
                        <span className="info-label">Brand:</span>
                        <span className="info-value">{labelBrand.name || "--"}</span>
                        <br />
                        <span className="info-label">Season:</span>
                        <span className="info-value">{seasonName || "--"}</span>
                        <br />
                        <span className="info-label">Scope:</span>
                        <span className="info-value">{scope.name || "--"}</span>
                    </div>
                </div>

                {/* Comparable season card */}
                <div className="context-card season-info">
                    <div className="info-details">
                        <span className="info-label">Comparable season:</span>
                        <span className="info-value">{comparableSeasonName || "--"}</span>
                        <br />
                        <span className="info-label">Requested on:</span>
                        <span className="info-value">{formatBuyingIntelDate(requestedOn)}</span>
                        <br />
                        <span className="info-label">Stage:</span>
                        <span className="info-value">{stage || "--"}</span>
                    </div>
                </div>

                {/* Budget KPI card */}
                <div className="context-card budget-info">
                    <div className="kpi-widget">
                        <div className="kpi-header">
                            <span className="kpi-label">BUDGET</span>
                            <InfoCircle className="kpi-info-icon" />
                        </div>
                        <div className="kpi-value">
                            <span className="kpi-value-main">{formatBuyingIntelNumber(budget.value)}</span>
                            <span className="kpi-value-unit">{budget.currency || ""}</span>
                        </div>
                        <div className="kpi-delta">
                            {formatBuyingIntelPercentage(budget.changeVsComparablePercentage, { signed: true })}
                        </div>
                    </div>
                </div>

                {/* Turnover + Gross Margin KPI card (double) */}
                <div className="context-card context-card--double-kpi margin-info">
                    <div className="kpi-pair">
                        <div className="kpi-widget">
                            <div className="kpi-header">
                                <span className="kpi-label">EST TURNOVER</span>
                                <InfoCircle className="kpi-info-icon" />
                            </div>
                            <div className="kpi-value">
                                <span className="kpi-value-main">
                                    {formatBuyingIntelNumber(estimatedTurnover.value)}
                                </span>
                                <span className="kpi-value-unit">{estimatedTurnover.currency || ""}</span>
                            </div>
                            <div className="kpi-delta">
                                {formatBuyingIntelPercentage(estimatedTurnover.changeVsComparablePercentage, {
                                    signed: true
                                })}
                            </div>
                        </div>
                        <div className="kpi-widget">
                            <div className="kpi-header">
                                <span className="kpi-label">EST GROSS MARGIN</span>
                                <InfoCircle className="kpi-info-icon" />
                            </div>
                            <div className="kpi-value">
                                <span className="kpi-value-main">
                                    {formatBuyingIntelNumber(estimatedGrossMargin.value)}
                                </span>
                                <span className="kpi-value-unit">{estimatedGrossMargin.currency || ""}</span>
                            </div>
                            <div className="kpi-delta">
                                {formatBuyingIntelPercentage(estimatedGrossMargin.changeVsComparablePercentage, {
                                    signed: true
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Order Score KPI card */}
                <div className="context-card order-score-info">
                    <div className="kpi-widget">
                        <div className="kpi-header">
                            <span className="kpi-label">ORDER SCORE</span>
                            <TooltipSimple
                                header="Order score"
                                title={orderScore.explanation}
                                placement="top"
                                centered={true}>
                                <InfoCircle className="kpi-info-icon" style={{ cursor: "pointer" }} />
                            </TooltipSimple>
                        </div>
                        <div className="kpi-value">
                            <span className="kpi-value-main">
                                {formatBuyingIntelNumber(orderScore.value, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 1
                                })}
                            </span>
                        </div>
                        <div className="kpi-status kpi-status--warning">{orderScore.status || "--"}</div>
                    </div>
                </div>

                {/* Action buttons card */}
                <div className="context-card context-card--actions actions-info">
                    <div className="actions-row">
                        <button type="button" className="action-btn" disabled aria-label="Files unavailable">
                            <File06 className="action-icon" />
                        </button>
                        <button type="button" className="action-btn" disabled aria-label="Overview unavailable">
                            <PresentationChart01 className="action-icon" />
                        </button>
                        <button type="button" className="action-btn" disabled aria-label="Order unavailable">
                            <Scale02 className="action-icon" />
                        </button>
                        <button type="button" className="action-btn" disabled aria-label="Forecast unavailable">
                            <LineChartUp01 className="action-icon" />
                        </button>
                    </div>
                </div>
            </StyledContextGrid>
        </FormContainer>
    );
}
